import React, { useEffect, useState, useRef } from 'react';
import { useEditor, EditorContent, useEditorState, ReactNodeViewRenderer } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import CodeBlockWithCaption from './extensions/CodeBlockWithCaption';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import Dropcursor from '@tiptap/extension-dropcursor';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Underline } from '@tiptap/extension-underline';
import { Highlight } from '@tiptap/extension-highlight';
import { TaskList } from '@tiptap/extension-task-list';
import { TaskItem } from '@tiptap/extension-task-item';
import { TextAlign } from '@tiptap/extension-text-align';
import { Typography } from '@tiptap/extension-typography';
import { CharacterCount } from '@tiptap/extension-character-count';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { Mention } from '@tiptap/extension-mention';
import { Mathematics } from '@tiptap/extension-mathematics';
import { Emoji } from '@tiptap/extension-emoji';
import { Details, DetailsSummary, DetailsContent } from '@tiptap/extension-details';
import { Markdown } from '@tiptap/markdown';
import 'katex/dist/katex.min.css';
import { createLowlight, all } from 'lowlight';
import ImageWithSize from './extensions/ImageWithSize';
import CodeBlockComponent from './CodeBlockComponent';
import {
  Bold,
  Italic,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Minus,
  Link as LinkIcon,
  Image as ImageIcon,
  Code,
  Code2,
  Undo,
  Redo,
  AlignLeft,
  AlignRight,
  AlignCenter,
  FileText,
  Table as TableIcon,
  Plus,
  Trash2,
  Underline as UnderlineIcon,
  Highlighter,
  ListChecks,
  AlignJustify,
  Subscript as SubscriptIcon,
  Superscript as SuperscriptIcon,
  AtSign,
  Sigma,
  Smile,
  ChevronRight,
} from 'lucide-react';
import ImageModal from './ImageModal';
import LinkModal from './LinkModal';
import IframeModal from './IframeModal';
import MarkdownModal from './MarkdownModal';
import Iframe from './extensions/Iframe';

// Créer une instance lowlight avec tous les langages
const lowlight = createLowlight(all);

// Composant MenuBar optimisé avec useEditorState
const MenuBar = ({ editor }) => {
  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      if (!ctx.editor) return null;

      // Vérifier si une image est sélectionnée et récupérer sa classe d'alignement
      const isImageSelected = ctx.editor.isActive('image');
      let imageFloatClass = 'float-none';
      if (isImageSelected) {
        const { state } = ctx.editor;
        const { selection } = state;
        const node = state.doc.nodeAt(selection.from);
        if (node && node.type.name === 'image') {
          imageFloatClass = node.attrs.class || 'float-none';
        }
      }

      return {
        isBold: ctx.editor.isActive('bold') ?? false,
        canBold: ctx.editor.can().chain().toggleBold().run() ?? false,
        isItalic: ctx.editor.isActive('italic') ?? false,
        canItalic: ctx.editor.can().chain().toggleItalic().run() ?? false,
        isStrike: ctx.editor.isActive('strike') ?? false,
        canStrike: ctx.editor.can().chain().toggleStrike().run() ?? false,
        isHeading1: ctx.editor.isActive('heading', { level: 1 }) ?? false,
        isHeading2: ctx.editor.isActive('heading', { level: 2 }) ?? false,
        isHeading3: ctx.editor.isActive('heading', { level: 3 }) ?? false,
        isBulletList: ctx.editor.isActive('bulletList') ?? false,
        isOrderedList: ctx.editor.isActive('orderedList') ?? false,
        isCodeBlock: ctx.editor.isActive('codeBlock') ?? false,
        isBlockquote: ctx.editor.isActive('blockquote') ?? false,
        isLink: ctx.editor.isActive('link') ?? false,
        canUndo: ctx.editor.can().chain().undo().run() ?? false,
        canRedo: ctx.editor.can().chain().redo().run() ?? false,
        isImageSelected,
        imageFloatClass,
        isInTable: ctx.editor.isActive('table') ?? false,
        isUnderline: ctx.editor.isActive('underline') ?? false,
        isHighlight: ctx.editor.isActive('highlight') ?? false,
        isTaskList: ctx.editor.isActive('taskList') ?? false,
        isSubscript: ctx.editor.isActive('subscript') ?? false,
        isSuperscript: ctx.editor.isActive('superscript') ?? false,
        isAlignLeft: ctx.editor.isActive({ textAlign: 'left' }) ?? false,
        isAlignCenter: ctx.editor.isActive({ textAlign: 'center' }) ?? false,
        isAlignRight: ctx.editor.isActive({ textAlign: 'right' }) ?? false,
        isAlignJustify: ctx.editor.isActive({ textAlign: 'justify' }) ?? false,
        characterCount: ctx.editor.storage?.characterCount?.characters?.() ?? 0,
        wordCount: ctx.editor.storage?.characterCount?.words?.() ?? 0,
      };
    },
  });

  if (!editorState) return null;

  return (
    <div className="tiptap-toolbar">
      <div className="toolbar-group">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editorState.canBold}
          className={editorState.isBold ? 'is-active' : ''}
          title="Gras"
        >
          <Bold size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editorState.canItalic}
          className={editorState.isItalic ? 'is-active' : ''}
          title="Italique"
        >
          <Italic size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          disabled={!editorState.canStrike}
          className={editorState.isStrike ? 'is-active' : ''}
          title="Barré"
        >
          <Strikethrough size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={editorState.isUnderline ? 'is-active' : ''}
          title="Souligné"
        >
          <UnderlineIcon size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          className={editorState.isHighlight ? 'is-active' : ''}
          title="Surligner"
        >
          <Highlighter size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleSubscript().run()}
          className={editorState.isSubscript ? 'is-active' : ''}
          title="Indice (X₂)"
        >
          <SubscriptIcon size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleSuperscript().run()}
          className={editorState.isSuperscript ? 'is-active' : ''}
          title="Exposant (X²)"
        >
          <SuperscriptIcon size={18} />
        </button>
      </div>

      <div className="toolbar-divider"></div>

      <div className="toolbar-group">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={editorState.isHeading1 ? 'is-active' : ''}
          title="Titre 1"
        >
          <Heading1 size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={editorState.isHeading2 ? 'is-active' : ''}
          title="Titre 2"
        >
          <Heading2 size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={editorState.isHeading3 ? 'is-active' : ''}
          title="Titre 3"
        >
          <Heading3 size={18} />
        </button>
      </div>

      <div className="toolbar-divider"></div>

      <div className="toolbar-group">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editorState.isBulletList ? 'is-active' : ''}
          title="Liste à puces"
        >
          <List size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editorState.isOrderedList ? 'is-active' : ''}
          title="Liste numérotée"
        >
          <ListOrdered size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleTaskList().run()}
          className={editorState.isTaskList ? 'is-active' : ''}
          title="Liste à cocher"
        >
          <ListChecks size={18} />
        </button>
      </div>

      <div className="toolbar-divider"></div>

      <div className="toolbar-group">
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={editorState.isAlignLeft ? 'is-active' : ''}
          title="Aligner à gauche"
        >
          <AlignLeft size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={editorState.isAlignCenter ? 'is-active' : ''}
          title="Centrer"
        >
          <AlignCenter size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={editorState.isAlignRight ? 'is-active' : ''}
          title="Aligner à droite"
        >
          <AlignRight size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          className={editorState.isAlignJustify ? 'is-active' : ''}
          title="Justifier"
        >
          <AlignJustify size={18} />
        </button>
      </div>

      <div className="toolbar-divider"></div>

      <div className="toolbar-group">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          className={editorState.isCodeBlock ? 'is-active' : ''}
          title="Bloc de code"
        >
          <Code size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={editorState.isBlockquote ? 'is-active' : ''}
          title="Citation"
        >
          <Quote size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Ligne horizontale"
        >
          <Minus size={18} />
        </button>
      </div>

      <div className="toolbar-divider"></div>

      <div className="toolbar-group">
        <LinkButton editor={editor} isActive={editorState.isLink} />
        <ImageButton editor={editor} />
        <IframeButton editor={editor} />
        <MarkdownImportButton editor={editor} />
        <button
          type="button"
          onClick={() => {
            const id = window.prompt('Identifiant à mentionner (ex: pedrokarim) :');
            if (id) {
              editor.chain().focus().insertContent({
                type: 'mention',
                attrs: { id, label: id },
              }).insertContent(' ').run();
            }
          }}
          title="Insérer une mention (@id)"
        >
          <AtSign size={18} />
        </button>
        <button
          type="button"
          onClick={() => {
            const formula = window.prompt('Formule LaTeX (ex: x^2 + y^2 = z^2) :');
            if (formula) {
              editor.chain().focus().insertContent({
                type: 'inlineMath',
                attrs: { latex: formula },
              }).run();
            }
          }}
          title="Insérer une formule mathématique (LaTeX)"
        >
          <Sigma size={18} />
        </button>
        <button
          type="button"
          onClick={() => {
            const emoji = window.prompt('Emoji à insérer (colle un caractère emoji) :');
            if (emoji) editor.chain().focus().insertContent(emoji).run();
          }}
          title="Insérer un emoji"
        >
          <Smile size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setDetails().run()}
          title="Insérer un bloc dépliable (details)"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="toolbar-divider"></div>

      <div className="toolbar-group">
        <button
          type="button"
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          title="Insérer un tableau (3x3)"
        >
          <TableIcon size={18} />
        </button>
        {editorState.isInTable && (
          <>
            <button
              type="button"
              onClick={() => editor.chain().focus().addRowAfter().run()}
              title="Ajouter une ligne en dessous"
            >
              <Plus size={14} />Ln
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().addColumnAfter().run()}
              title="Ajouter une colonne à droite"
            >
              <Plus size={14} />Col
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().deleteRow().run()}
              title="Supprimer la ligne"
            >
              <Trash2 size={14} />Ln
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().deleteColumn().run()}
              title="Supprimer la colonne"
            >
              <Trash2 size={14} />Col
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().deleteTable().run()}
              title="Supprimer le tableau"
            >
              <Trash2 size={18} />
            </button>
          </>
        )}
      </div>

      {editorState.isImageSelected && (
        <>
          <div className="toolbar-divider"></div>
          <div className="toolbar-group">
            <button
              type="button"
              onClick={() => editor.chain().focus().updateAttributes('image', { class: 'float-left' }).run()}
              className={editorState.imageFloatClass === 'float-left' ? 'is-active' : ''}
              title="Aligner à gauche (texte autour)"
            >
              <AlignLeft size={18} />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().updateAttributes('image', { class: 'float-none' }).run()}
              className={editorState.imageFloatClass === 'float-none' ? 'is-active' : ''}
              title="Centrer (pas de texte autour)"
            >
              <AlignCenter size={18} />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().updateAttributes('image', { class: 'float-right' }).run()}
              className={editorState.imageFloatClass === 'float-right' ? 'is-active' : ''}
              title="Aligner à droite (texte autour)"
            >
              <AlignRight size={18} />
            </button>
          </div>
        </>
      )}

      <div className="toolbar-divider"></div>

      <div className="toolbar-group">
        <button
          type="button"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editorState.canUndo}
          title="Annuler"
        >
          <Undo size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editorState.canRedo}
          title="Refaire"
        >
          <Redo size={18} />
        </button>
      </div>
    </div>
  );
};

// Composant bouton Link avec modal
const LinkButton = ({ editor, isActive }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleInsertLink = (url) => {
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className={isActive ? 'is-active' : ''}
        title="Lien"
      >
        <LinkIcon size={18} />
      </button>
      <LinkModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onInsert={handleInsertLink}
      />
    </>
  );
};

// Composant bouton Image avec modal
const ImageButton = ({ editor }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleInsertImage = (url) => {
    if (url) {
      // Charger l'image pour obtenir ses dimensions et les limiter
      const img = document.createElement('img');
      img.onload = () => {
        const maxWidth = 800;
        const maxHeight = 600;
        let width = img.width;
        let height = img.height;

        // Calculer les dimensions en préservant le ratio d'aspect
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        // Insérer l'image avec les dimensions limitées en utilisant insertContent
        editor.chain().focus().insertContent({
          type: 'image',
          attrs: {
            src: url,
            width: `${width}`,
            height: `${height}`
          }
        }).run();
      };
      img.onerror = () => {
        // Si l'image ne peut pas être chargée, l'insérer quand même avec des dimensions par défaut
        editor.chain().focus().insertContent({
          type: 'image',
          attrs: {
            src: url,
            width: '800',
            height: '600'
          }
        }).run();
      };
      img.src = url;
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        title="Image"
      >
        <ImageIcon size={18} />
      </button>
      <ImageModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onInsert={handleInsertImage}
      />
    </>
  );
};

// Composant bouton Iframe avec modal
const IframeButton = ({ editor }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleInsertIframe = (options) => {
    if (options && options.src) {
      editor.chain().focus().setIframe(options).run();
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        title="Iframe"
      >
        <Code2 size={18} />
      </button>
      <IframeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onInsert={handleInsertIframe}
      />
    </>
  );
};

// Composant bouton Import Markdown avec modal
const MarkdownImportButton = ({ editor }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleImportMarkdown = (markdownContent) => {
    if (markdownContent) {
      try {
        // Utiliser setContent avec contentType: 'markdown' pour convertir le Markdown
        editor.commands.setContent(markdownContent, { contentType: 'markdown' });
      } catch (error) {
        console.error('Erreur lors de l\'import du Markdown:', error);
        alert('Erreur lors de l\'import du Markdown. Veuillez vérifier que le contenu est valide.');
      }
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        title="Importer du Markdown"
      >
        <FileText size={18} />
      </button>
      <MarkdownModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onInsert={handleImportMarkdown}
      />
    </>
  );
};

const TipTapEditor = ({ content, onChange, placeholder = "Commencez à écrire..." }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
        codeBlock: false, // Désactiver le codeBlock par défaut pour utiliser CodeBlockLowlight
      }),
      CodeBlockWithCaption.extend({
        addNodeView() {
          return ReactNodeViewRenderer(CodeBlockComponent);
        },
      }).configure({ lowlight }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'editor-link',
        },
      }),
      ImageWithSize.configure({
        HTMLAttributes: {
          class: 'editor-image',
        },
        inline: true,
        allowBase64: true,
        resize: {
          enabled: true,
          directions: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
          minWidth: 50,
          minHeight: 50,
          alwaysPreserveAspectRatio: true,
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      Dropcursor.configure({
        color: '#667eea',
        width: 2,
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: { class: 'editor-table' },
      }),
      TableRow,
      TableHeader,
      TableCell,
      Underline,
      Highlight.configure({ multicolor: false, HTMLAttributes: { class: 'editor-highlight' } }),
      TaskList.configure({ HTMLAttributes: { class: 'editor-task-list' } }),
      TaskItem.configure({ nested: true, HTMLAttributes: { class: 'editor-task-item' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'], alignments: ['left', 'center', 'right', 'justify'] }),
      Typography,
      CharacterCount,
      Subscript,
      Superscript,
      Mention.configure({
        HTMLAttributes: { class: 'editor-mention' },
        renderText: ({ node }) => `@${node.attrs.label || node.attrs.id}`,
      }),
      Mathematics.configure({
        // Délimiteurs LaTeX standards : $...$ inline, $$...$$ block
        regex: /\$([^$]+)\$/g,
      }),
      Emoji,
      Details.configure({
        HTMLAttributes: { class: 'editor-details' },
        persist: true,
      }),
      DetailsSummary,
      DetailsContent,
      Iframe,
      Markdown,
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      const json = editor.getJSON();
      onChange(json);
    },
    editorProps: {
      attributes: {
        class: 'tiptap',
      },
    },
  });

  // Mettre à jour le contenu quand la prop content change (ex: chargement d'un article)
  useEffect(() => {
    if (editor && content !== undefined) {
      const currentContent = editor.getJSON();
      let newContent;

      try {
        // Essayer de parser le contenu s'il est une string
        if (typeof content === 'string') {
          if (content.trim() === '') {
            newContent = '';
          } else {
            newContent = JSON.parse(content);
          }
        } else {
          newContent = content;
        }
      } catch (e) {
        // Si le contenu n'est pas du JSON valide, on le traite comme du texte
        newContent = typeof content === 'string' ? content : '';
      }

      // Comparer les contenus pour éviter les mises à jour infinies
      const currentContentStr = JSON.stringify(currentContent);
      const newContentStr = JSON.stringify(newContent || '');

      if (currentContentStr !== newContentStr) {
        editor.commands.setContent(newContent || '');
      }
    }
  }, [content, editor]);

  if (!editor) {
    return <div>Chargement de l'éditeur...</div>;
  }

  return (
    <div className="tiptap-wrapper">
      {editor && (
        <BubbleMenu className="bubble-menu" editor={editor}>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={editor.isActive('bold') ? 'is-active' : ''}
            title="Gras"
          >
            <Bold size={18} />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={editor.isActive('italic') ? 'is-active' : ''}
            title="Italique"
          >
            <Italic size={18} />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={editor.isActive('strike') ? 'is-active' : ''}
            title="Barré"
          >
            <Strikethrough size={18} />
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            className={editor.isActive('codeBlock') ? 'is-active' : ''}
            title="Bloc de code"
          >
            <Code size={18} />
          </button>
        </BubbleMenu>
      )}

      <MenuBar editor={editor} />
      <div className="tiptap-content">
        <EditorContent editor={editor} />
      </div>
      <CharacterCounter editor={editor} />
    </div>
  );
};

// Affiche le compteur de mots/caractères en bas de l'éditeur
const CharacterCounter = ({ editor }) => {
  const stats = useEditorState({
    editor,
    selector: (ctx) => {
      if (!ctx.editor) return null;
      const cc = ctx.editor.storage?.characterCount;
      if (!cc) return null;
      return {
        characters: cc.characters?.() ?? 0,
        words: cc.words?.() ?? 0,
      };
    },
  });

  if (!stats) return null;

  return (
    <div className="tiptap-counter" aria-live="polite">
      {stats.characters} caractère{stats.characters > 1 ? 's' : ''} · {stats.words} mot{stats.words > 1 ? 's' : ''}
    </div>
  );
};

export default TipTapEditor;
